import React, { useState, useEffect } from "react";
import {
  Box, Button, Card, CardContent, CardMedia,Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, Typography, MenuItem, Switch,
  FormControlLabel, Container, Divider, Checkbox, List, ListItem, ListItemText, FormControl, InputLabel, Select,
  styled
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useLocation } from "react-router-dom";
import { db } from "../../firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where, getDoc, setDoc } from "firebase/firestore";
import Alerts from "../../components/Alerts";
import * as XLSX from "xlsx";

const CATEGORIES = ["Starters", "Main Course", "Beverages", "Desserts", "Others"];

const StyledCard = styled(Card)(({ theme }) => ({
  height: "380px",
  width: "300px",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  transition: "transform 0.3s, box-shadow 0.3s",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: theme.shadows[6],
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  height: "380px",
  width: "300px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.grey[100],
  transition: "transform 0.3s, box-shadow 0.3s",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: theme.shadows[6],
    backgroundColor: theme.palette.grey[200],
  },
}));

const formatPriceForUI = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  return Number.isInteger(num) ? num.toString() : num.toString();
};

const toTitleCase = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const generateItemCode = (name) => {
  if (!name) return "";

  const words = name.trim().split(/\s+/);

  let code = words
    .map(word => word[0])
    .join("")
    .toUpperCase();

  return code.slice(0, 5); // max length 5
};

export default function ManageMenu() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const companyCode = queryParams.get("code");
  const isPureVeg = queryParams.get("isPureVeg") === "true";
  const [originalGstPlan, setOriginalGstPlan] = useState("");
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [gstPlans, setGstPlans] = useState([]);
  const [selectedUnitCode, setSelectedUnitCode] = useState("all");
  const [kitchenStaff, setKitchenStaff] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [detailDialog, setDetailDialog] = useState({ open: false, item: null });
  const [importDialog, setImportDialog] = useState({ open: false, items: [], selected: [] });
  const [pendingImportItem, setPendingImportItem] = useState(null);
  const [priceCategories, setPriceCategories] = useState([]);
  const [categoryPrices, setCategoryPrices] = useState({});
  const [categories, setCategories] = useState([]);
  const [menuTimings, setMenuTimings] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    itemCode: "",
    category: "",
    description: "",
    ingredients: "",
    price: "",
    type: isPureVeg ? "veg" : "veg",
    available: true,
    timing: "",
    imageUrl: "",
    staff: "",
    unitCode: "",
    gstPlan: ""
  });
  const [gstUpdateDialog, setGstUpdateDialog] = useState({
    open: false,
    applyToCategory: false,
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [excelPreviewDialog, setExcelPreviewDialog] = useState(false);
  const [excelPreviewItems, setExcelPreviewItems] = useState([]);
  const [isExcelPreviewEdit, setIsExcelPreviewEdit] = useState(false);
  const [editingPreviewIndex, setEditingPreviewIndex] = useState(null);
  const [excelImportDialog, setExcelImportDialog] = useState(false);

const [importableItems, setImportableItems] = useState([]);
const [existingItems, setExistingItems] = useState([]);
const [rejectedItems, setRejectedItems] = useState([]);

const [selectedImportItems, setSelectedImportItems] = useState([]);

  const fetchCategories = async () => {
  try {
    if (!companyCode) return;

    let q;

    if (selectedUnitCode === "all") {
      // Fetch all categories for the company
      q = query(
        collection(db, "categories"),
        where("companyCode", "==", companyCode)
      );
    } else {
      // Fetch categories only for selected unit
      q = query(
        collection(db, "categories"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", selectedUnitCode)
      );
    }

    const snapshot = await getDocs(q);

    const list = snapshot.docs.map(doc => doc.data().name);

    setCategories(list);

    console.log(
      `Fetched categories for companyCode: ${companyCode}, unitCode: ${selectedUnitCode}`,
      list
    );
  } catch (error) {
    console.error("Error fetching categories:", error);
  }
};
  useEffect(() => {
    if (!companyCode) {
      setSnackbar({ open: true, message: "Company code missing. Some features may not work correctly.", severity: "warning" });
    } else {
      fetchUnits();
      fetchGstPlans();
      fetchCompanyItems();
      fetchKitchenStaff(selectedUnitCode === "all" ? null : selectedUnitCode);
      fetchPriceCategories();
      fetchCategories();
      fetchMenuTimings();
    }
  }, [companyCode, selectedUnitCode]);

  useEffect(() => {
    // Fetch kitchen staff when formData.unitCode changes in the add/edit dialog
    if (open && formData.unitCode) {
      fetchKitchenStaff(formData.unitCode);
    }
  }, [formData.unitCode, open]);

  useEffect(() => {
    if (companyCode) {
      fetchMenuTimings();
    }
  }, [companyCode, selectedUnitCode]);

  const fetchUnits = async () => {
    try {
      const unitsQuery = query(collection(db, "units"), where("companyCode", "==", companyCode));
      const unitsSnapshot = await getDocs(unitsQuery);
      const unitList = unitsSnapshot.docs.map(doc => ({
        unitCode: doc.id,
        name: doc.data().unitName || doc.id,
        unitType: doc.data().unitType || "Unknown"
      }));
      setUnits(unitList);
      console.log("ManageMenu: Fetched units:", unitList);
      if (unitList.length === 0) {
        console.warn("ManageMenu: No units found for companyCode:", companyCode);
      }
    } catch (error) {
      console.error("ManageMenu: Error fetching units:", error);
      setSnackbar({ open: true, message: "Failed to load units.", severity: "error" });
    }
  };

  const fetchGstPlans = async () => {
    try {
      const gstQuery = query(collection(db, "gstPlans"), where("companyCode", "==", companyCode));
      const gstSnapshot = await getDocs(gstQuery);
      const gstList = gstSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setGstPlans(gstList);
      console.log(`ManageMenu: Fetched ${gstList.length} GST plans for companyCode: ${companyCode}`);
      if (gstList.length === 0) {
        console.warn("ManageMenu: No GST plans found for companyCode:", companyCode);
      }
    } catch (error) {
      console.error("ManageMenu: Error fetching GST plans:", error);
      setSnackbar({ open: true, message: "Failed to load GST plans.", severity: "error" });
    }
  };

  const fetchCompanyItems = async () => {
  try {
    const q =
      selectedUnitCode === "all"
        ? query(collection(db, "companymenu"), where("companyCode", "==", companyCode))
        : query(
            collection(db, "companymenu"),
            where("companyCode", "==", companyCode),
            where("unitCode", "==", selectedUnitCode)
          );

    const snapshot = await getDocs(q);

    const data = snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter(
        (item) =>
          item.unitCode &&
          (units.length === 0 || units.some((unit) => unit.unitCode === item.unitCode))
      );

    setItems(data);

    console.log(
      `ManageMenu: Fetched ${data.length} items for companyCode: ${companyCode}, unitCode: ${selectedUnitCode}`
    );
  } catch (error) {
    console.error("ManageMenu: Error fetching company menu items:", error, {
      code: error.code,
      message: error.message,
    });

    setSnackbar({
      open: true,
      message: `Failed to load menu items: ${error.message}`,
      severity: "error",
    });
  }
};

  const fetchKitchenStaff = async (unitCode) => {
    try {
      const q = unitCode
        ? query(collection(db, "users"), where("role", "==", "kitchen_staff"), where("unitCode", "==", `${unitCode}`))
        : query(collection(db, "users"), where("role", "==", "kitchen_staff"), where("companyCode", "==", `${companyCode}`));
      const snapshot = await getDocs(q);
      const staff = snapshot.docs.map((doc) => doc.data().userId);
      setKitchenStaff(staff);
      console.log(`ManageMenu: Fetched kitchen staff for ${unitCode ? `unitCode: ${unitCode}` : `companyCode: ${companyCode}`}:`, staff);
      if (staff.length === 0) {
        console.warn(`ManageMenu: No kitchen staff found for ${unitCode ? `unitCode: ${unitCode}` : `companyCode: ${companyCode}`}`);
      }
    } catch (error) {
      console.error("ManageMenu: Error fetching kitchen staff:", error);
      setSnackbar({ open: true, message: "Failed to load kitchen staff.", severity: "error" });
    }
  };

  const fetchMasterMenu = async () => {
    try {
      const companyQuery = query(collection(db, "companymenu"), where("companyCode", "==", companyCode));
      const companySnapshot = await getDocs(companyQuery);
      const companyItemNames = companySnapshot.docs.map((doc) => doc.data().name);

      const snapshot = await getDocs(collection(db, "menu"));
      let data = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((item) => !companyItemNames.includes(item.name));

      if (isPureVeg) {
        data = data.filter((item) => item.type === "veg" || item.category === "Beverages");
      }

      setImportDialog((prev) => ({ ...prev, items: data, selected: [] }));
      console.log("ManageMenu: Fetched master menu:", data);
    } catch (error) {
      console.error("ManageMenu: Error fetching master menu:", error);
      setSnackbar({ open: true, message: "Failed to load master menu.", severity: "error" });
    }
  };

  useEffect(() => {
  if (!open) return;

  if (formData.unitCode && formData.unitCode !== "all") {
    fetchPriceCategories(formData.unitCode);
  } else {
    setPriceCategories([]);
  }
}, [formData.unitCode, open]);

const fetchPriceCategories = async (unitCode) => {
  try {
    if (!companyCode || !unitCode) return;

    const q = query(
      collection(db, "priceCategories"),
      where("companyCode", "==", companyCode),
      where("unitCode", "==", unitCode)
    );

    const snapshot = await getDocs(q);
    const list = snapshot.docs.map(doc => doc.data());

    setPriceCategories(list);

    console.log("Fetched price categories for dialog unit:", unitCode, list);

  } catch (error) {
    console.error("Error fetching price categories:", error);
  }
};
 
  useEffect(() => {
  if (!open) return;

  const basePrice = Number(formData.price);

  if (!basePrice || isNaN(basePrice)) {
    setCategoryPrices({});
    return;
  }

  setCategoryPrices(prev => {
    const updatedPrices = { ...prev };

    priceCategories.forEach(cat => {
      // Auto calculated categories
      if (cat.percentageIncrement > 0) {
        updatedPrices[cat.priceCategoryName] = Number(
          basePrice + (basePrice * cat.percentageIncrement) / 100
        ).toFixed(2);
      }

      // Manual categories remain untouched
      if (
        cat.percentageIncrement === 0 &&
        updatedPrices[cat.priceCategoryName] === undefined
      ) {
        updatedPrices[cat.priceCategoryName] = "";
      }
    });

    return updatedPrices;
  });

}, [formData.price, priceCategories, open]);

  const handleOpen = (category = "") => {
    setFormData({
      name: "",
      itemCode: "",
      category: category,
      description: "",
      ingredients: "",
      price: "",
      type: isPureVeg ? "veg" : "veg",
      available: true,
      timing: "",
      imageUrl: "",
      staff: "",
      unitCode: selectedUnitCode === "all" ? "" : selectedUnitCode,
      gstPlan: ""
    });
    setCategoryPrices({});
    setIsEditing(false);
    setEditItemId(null);
    setOpen(true);
  };

  const handleEditClick = (item, isImport = false) => {
    setIsEditing(!isImport);
    setEditItemId(isImport ? null : item.id);
    const defaultUnitCode = selectedUnitCode === "all" ? (units.length > 0 ? units[0].unitCode : "") : selectedUnitCode;
    setFormData({
      name: item.name,
      itemCode: item.itemCode || "",
      category: item.category,
      description: item.description || "",
      ingredients: item.ingredients || "",
      price: item.price.toString(), // Use stored price (includes GST for non-inclusive plans)
      type: item.type,
      available: item.available,
      timing: item.timing || "",
      imageUrl: item.imageUrl || "",
      staff: item.staff || "",
      unitCode: item.unitCode || defaultUnitCode,
      gstPlan: item.gstPlan || ""
    });

    // Store original GST plan
    setOriginalGstPlan(item.gstPlan || "");
    //setCategoryPrices(item.categoryPrices || {});
    const formattedCategoryPrices = {};

    Object.entries(item.categoryPrices || {}).forEach(([key, value]) => {
      formattedCategoryPrices[key] = formatPriceForUI(value);
    });

    setCategoryPrices(formattedCategoryPrices);
      setDetailDialog({ open: false, item: null });
      setOpen(true);
    };

  const handleClose = () => {
    setOpen(false);
    setIsEditing(false);
    setEditItemId(null);
    setFormData({
      name: "",
      itemCode: "",
      category: "",
      description: "",
      ingredients: "",
      price: "",
      type: isPureVeg ? "veg" : "veg",
      available: true,
      timing: "",
      imageUrl: "",
      staff: "",
      unitCode: "",
      gstPlan: ""
    });
    setPendingImportItem(null);
    setKitchenStaff([]);
    setIsExcelPreviewEdit(false);
setEditingPreviewIndex(null);
  };

  const handleImportOpen = () => {
    if (!companyCode) {
      setSnackbar({ open: true, message: "Cannot import menu without a company code.", severity: "error" });
      return;
    }
    fetchMasterMenu();
    setImportDialog((prev) => ({ ...prev, open: true }));
  };

  const handleImportClose = () => {
    setImportDialog({ open: false, items: [], selected: [] });
  };

  const handleImportSelect = (itemId) => {
    setImportDialog((prev) => ({
      ...prev,
      selected: prev.selected.includes(itemId)
        ? prev.selected.filter((id) => id !== itemId)
        : [...prev.selected, itemId],
    }));
  };

  const handleImportConfirm = async () => {
    try {
      const selectedItems = importDialog.items.filter((item) =>
        importDialog.selected.includes(item.id)
      );
      if (selectedItems.length === 0) {
        setSnackbar({ open: true, message: "No items selected for import.", severity: "warning" });
        return;
      }

      const firstItem = selectedItems[0];
      setPendingImportItem({
        items: selectedItems.slice(1),
        current: firstItem,
      });
      handleEditClick(firstItem, true);
      setImportDialog((prev) => ({ ...prev, open: false, selected: [] }));
    } catch (error) {
      console.error("ManageMenu: Error initiating import:", error);
      setSnackbar({ open: true, message: "Failed to initiate import.", severity: "error" });
    }
  };

  const isItemCodeExists = async (itemCode) => {
  if (!itemCode) return false;

  const q = query(
    collection(db, "companymenu"),
    where("companyCode", "==", companyCode),
    where("itemCode", "==", itemCode)
  );

  const snapshot = await getDocs(q);

  // If editing, ignore current item
  if (isEditing) {
    return snapshot.docs.some(doc => doc.id !== editItemId);
  }

  return !snapshot.empty;
};

const calculateGSTValues = (price, gstPlan) => {
  const enteredPrice = parseFloat(price);

  let finalPrice = enteredPrice;
  let basePrice = enteredPrice;
  const gstPercentage = gstPlan.percentage;

  let cgstAmount = 0;
  let sgstAmount = 0;

  if (gstPlan.inclusive) {
    basePrice = enteredPrice * 100 / (100 + gstPercentage);

    const totalGstAmount = enteredPrice - basePrice;

    cgstAmount = totalGstAmount / 2;
    sgstAmount = totalGstAmount / 2;

    finalPrice = enteredPrice;
  } else {
    basePrice = enteredPrice;

    const totalGstAmount = enteredPrice * (gstPercentage / 100);

    cgstAmount = totalGstAmount / 2;
    sgstAmount = totalGstAmount / 2;

    finalPrice = enteredPrice + totalGstAmount;
  }

  return {
    price: parseFloat(finalPrice.toFixed(2)),
    basePrice: parseFloat(basePrice.toFixed(2)),
    gstPercentage,
    cgstAmount: parseFloat(cgstAmount.toFixed(2)),
    sgstAmount: parseFloat(sgstAmount.toFixed(2)),
  };
};

  const handleAdd = async () => {
    try {
      if (!companyCode) {
        setSnackbar({ open: true, message: "Cannot add item without a company code.", severity: "error" });
        return;
      }
      if (!formData.name || !formData.category || !formData.price || !formData.staff || !formData.unitCode || formData.unitCode === "" || !formData.gstPlan) {
        setSnackbar({
          open: true,
          message: "Please fill in required fields: Name, Category, Price, Staff User ID, Unit, and GST Plan",
          severity: "warning",
        });
        return;
      }

      // Check duplicate itemCode
const codeExists = await isItemCodeExists(formData.itemCode);

if (codeExists) {
  setSnackbar({
    open: true,
    message: "Item Code already exists. Please use a different code.",
    severity: "error",
  });
  return;
}
      if (isPureVeg && formData.type === "nonveg") {
        setSnackbar({
          open: true,
          message: "Cannot add non-veg items for a pure veg company.",
          severity: "warning",
        });
        return;
      }

      let companyName = "Unknown Company";
      let unitName = "Unknown Unit";

      // Fetch company name
      const companyDocRef = doc(db, "companies", companyCode);
      const companyDoc = await getDoc(companyDocRef);
      if (companyDoc.exists()) {
        companyName = companyDoc.data().name || "Unknown Company";
      } else {
        console.warn(`ManageMenu: Company document not found for companyCode: ${companyCode}`);
      }

      // Fetch unit name
      if (formData.unitCode && units.some(unit => unit.unitCode === formData.unitCode)) {
        const unitDocRef = doc(db, "units", formData.unitCode);
        const unitDoc = await getDoc(unitDocRef);
        if (unitDoc.exists()) {
          unitName = unitDoc.data().unitName || "Unknown Unit";
        } else {
          console.warn(`ManageMenu: Unit document not found for unitCode: ${formData.unitCode}`);
        }
      } else {
        console.warn(`ManageMenu: Invalid or missing unitCode: ${formData.unitCode}`);
        setSnackbar({
          open: true,
          message: "Invalid unit selected. Please choose a valid unit.",
          severity: "error",
        });
        return;
      }

      // Calculate price and basePrice based on GST inclusivity
      const selectedGstPlan = gstPlans.find(plan => plan.gstName === formData.gstPlan);
      if (!selectedGstPlan) {
        setSnackbar({
          open: true,
          message: "Selected GST plan not found.",
          severity: "error",
        });
        return;
      }

      const enteredPrice = parseFloat(formData.price);
      let finalPrice = enteredPrice;
      let basePrice = enteredPrice;
      const gstPercentage = selectedGstPlan.percentage;
      let cgstAmount = 0;
      let sgstAmount = 0;

      if (selectedGstPlan.inclusive) {
        // For inclusive GST, calculate base price and derive CGST/SGST
        basePrice = enteredPrice * 100 / (100 + gstPercentage);
        finalPrice = enteredPrice; // Store the entered price as-is
        const totalGstAmount = enteredPrice - basePrice;
        cgstAmount = totalGstAmount / 2; // Half of GST as CGST
        sgstAmount = totalGstAmount / 2; // Half of GST as SGST
      } else {
        // For non-inclusive GST, add GST to the entered price and split into CGST/SGST
        basePrice = enteredPrice; // Entered price is the base price
        const totalGstAmount = enteredPrice * (gstPercentage / 100);
        cgstAmount = totalGstAmount / 2; // Half of GST as CGST
        sgstAmount = totalGstAmount / 2; // Half of GST as SGST
        finalPrice = enteredPrice + totalGstAmount; // Final price includes GST
      }

      //price categories handling
      const finalCategoryPrices = {};

      //finalCategoryPrices["Standard Price"] = Number(basePrice).toFixed(2);

      finalCategoryPrices["Standard Price"] = Number(finalPrice).toFixed(2);

      priceCategories
        .filter(cat => cat.priceCategoryName !== "Standard Price")
        .forEach(cat => {
          finalCategoryPrices[cat.priceCategoryName] = Number(
            categoryPrices[cat.priceCategoryName] ?? basePrice
          ).toFixed(2);
        });

      /*priceCategories.forEach(cat => {
        finalCategoryPrices[cat.priceCategoryName] = Number(
          categoryPrices[cat.priceCategoryName] ?? basePrice
        ).toFixed(2);
      });*/

      const newItem = {
        name: formData.name,
        itemCode: formData.itemCode,
        category: formData.category,
        description: formData.description || "",
        ingredients: formData.ingredients || "",
        price: parseFloat(finalPrice.toFixed(2)), // Store price with GST for non-inclusive
        basePrice: parseFloat(basePrice.toFixed(2)), // Store base price
        categoryPrices: finalCategoryPrices,
        type: formData.type,
        available: formData.available,
        timing: formData.available ? formData.timing : "",
        imageUrl: formData.imageUrl || "",
        staff: formData.staff,
        companyCode,
        companyName,
        unitCode: formData.unitCode,
        unitName,
        gstPlan: formData.gstPlan,
        gstPercentage,
        cgstAmount: parseFloat(cgstAmount.toFixed(2)), // Store CGST amount
        sgstAmount: parseFloat(sgstAmount.toFixed(2)), // Store SGST amount
        createdAt: new Date(),
      };

      
if (isEditing) {

  // GST changed
  if (originalGstPlan !== formData.gstPlan) {

    setGstUpdateDialog({
      open: true,
      applyToCategory: false,
      item: {
        id: editItemId,
        ...newItem
      },
    });

    return;
  }

  await updateDoc(doc(db, "companymenu", editItemId), {
    id: editItemId,
    ...newItem
  });

  setSnackbar({
    open: true,
    message: "Menu item updated successfully!",
    severity: "success"
  });

} else {

  const newDocRef = doc(collection(db, "companymenu"));

  await setDoc(newDocRef, {
    id: newDocRef.id,
    ...newItem
  });
  setSnackbar({ open: true, message: "Menu item added successfully!", severity: "success" });
}

      if (pendingImportItem && pendingImportItem.items.length > 0) {
        const nextItem = pendingImportItem.items[0];
        setPendingImportItem({
          items: pendingImportItem.items.slice(1),
          current: nextItem,
        });
        handleEditClick(nextItem, true);
      } else {
        handleClose();
        fetchCompanyItems();
      }
    } catch (error) {
      console.error("ManageMenu: Error saving item:", error);
      setSnackbar({ open: true, message: `Failed to save menu item: ${error.message}`, severity: "error" });
    }
  };

  const updateCategoryGST = async (applyToCategory) => {
    try {
  
      const item = gstUpdateDialog.item;
  
      // update current item first
      await updateDoc(doc(db, "companymenu", editItemId), item);
  
      if (applyToCategory) {
  
        const q = query(
          collection(db, "companymenu"),
          where("companyCode", "==", companyCode),
          where("category", "==", formData.category)
        );
  
        const snapshot = await getDocs(q);
  
        const selectedGstPlan = gstPlans.find(
          plan => plan.gstName === formData.gstPlan
        );
  
        for (const document of snapshot.docs) {
  
          if (document.id === editItemId) continue;
  
          const data = document.data();
  
          const currentPrice = Number(data.price);
  
  let basePrice = currentPrice;
  let cgstAmount = 0;
  let sgstAmount = 0;
  
  if (selectedGstPlan.inclusive) {
  
    basePrice =
      currentPrice * 100 / (100 + selectedGstPlan.percentage);
  
    const totalGstAmount = currentPrice - basePrice;
  
    cgstAmount = totalGstAmount / 2;
    sgstAmount = totalGstAmount / 2;
  
  } else {
  
    // keep selling price same
    basePrice =
      currentPrice / (1 + selectedGstPlan.percentage / 100);
  
    const totalGstAmount = currentPrice - basePrice;
  
    cgstAmount = totalGstAmount / 2;
    sgstAmount = totalGstAmount / 2;
  }
  
  await updateDoc(doc(db, "companymenu", document.id), {
    gstPlan: formData.gstPlan,
    gstPercentage: selectedGstPlan.percentage,
  
    // KEEP PRICE SAME
    price: parseFloat(currentPrice.toFixed(2)),
  
    basePrice: parseFloat(basePrice.toFixed(2)),
    cgstAmount: parseFloat(cgstAmount.toFixed(2)),
    sgstAmount: parseFloat(sgstAmount.toFixed(2)),
  });
        }
      }
  
      setSnackbar({
        open: true,
        message: applyToCategory
          ? "GST updated for all items in category."
          : "GST updated only for this item.",
        severity: "success",
      });
  
      setGstUpdateDialog({
        open: false,
        applyToCategory: false,
      });
  
      handleClose();
      fetchCompanyItems();
  
    } catch (error) {
      console.error(error);
  
      setSnackbar({
        open: true,
        message: "Failed to update GST.",
        severity: "error",
      });
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteDialog({ open: true, id });
  };

  /*const handleDeleteConfirm = async () => {
    try {
      await deleteDoc(doc(db, "companymenu", deleteDialog.id));
      fetchCompanyItems();
      setSnackbar({ open: true, message: "Item deleted successfully.", severity: "info" });
    } catch (error) {
      console.error("ManageMenu: Error deleting item:", error);
      setSnackbar({ open: true, message: "Failed to delete item.", severity: "error" });
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };*/
  const handleDeleteConfirm = async () => {
  try {
    // Get menu item
    const menuDoc = await getDoc(
      doc(db, "companymenu", deleteDialog.id)
    );

    if (!menuDoc.exists()) {
      return;
    }

    const menuItem = menuDoc.data();

    // Fetch open orders
    const ordersQuery = query(
      collection(db, "orders"),
      where("companyCode", "==", companyCode),
      where("orderStatus", "==", "Open")
    );

    const ordersSnapshot = await getDocs(ordersQuery);

    let itemUsedInOpenOrder = false;

    ordersSnapshot.forEach((orderDoc) => {
      const order = orderDoc.data();

      const found = (order.items || []).some(
        (item) =>
          item.itemCode === menuItem.itemCode
      );

      if (found) {
        itemUsedInOpenOrder = true;
      }
    });

    if (itemUsedInOpenOrder) {
      setSnackbar({
        open: true,
        severity: "warning",
        message:
          "Cannot delete this item because it exists in an open order.",
      });
      return;
    }

    await deleteDoc(doc(db, "companymenu", deleteDialog.id));

    fetchCompanyItems();

    setSnackbar({
      open: true,
      severity: "success",
      message: "Item deleted successfully.",
    });

  } catch (error) {
    console.error(error);

    setSnackbar({
      open: true,
      severity: "error",
      message: "Failed to delete item.",
    });
  } finally {
    setDeleteDialog({ open: false, id: null });
  }
};

  const handleDetailClick = (item) => {
    setDetailDialog({ open: true, item });
  };

  const handleDetailClose = () => {
    setDetailDialog({ open: false, item: null });
  };

  /*const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };*/
 const handleChange = (e) => {
  const { name, value, type, checked } = e.target;

  let newValue = type === "checkbox" ? checked : value;

  /*if (name === "name") {
    const titleName = toTitleCase(value);

    setFormData((prev) => ({
      ...prev,
      name: titleName,
      itemCode: generateItemCode(titleName)
    }));*/
    if (name === "name") {
  const titleName = toTitleCase(value);

  setFormData((prev) => ({
    ...prev,
    name: titleName,
    itemCode: titleName ? generateItemCode(titleName) : ""
  }));

  return;
}
   

  if (name === "available" && !checked) {
  setFormData(prev => ({
    ...prev,
    available: false,
    timing: ""
  }));
  return;
}

  if (name === "itemCode") {
    newValue = value.toUpperCase().slice(0, 5);
  }

  setFormData((prev) => ({
    ...prev,
    [name]: newValue
  }));

  
};

  const handleUnitChange = (e) => {
    setSelectedUnitCode(e.target.value);
    console.log("ManageMenu: Selected unitCode:", e.target.value);
  };

  const groupByUnitAndCategory = (items) => {
    const grouped = {};
    const unitGroups = selectedUnitCode === "all"
      ? units.reduce((acc, unit) => ({ ...acc, [unit.unitCode]: unit.name }), {})
      : { [selectedUnitCode]: units.find(u => u.unitCode === selectedUnitCode)?.name || "Unknown Unit" };

    Object.keys(unitGroups).forEach(unitCode => {
      grouped[unitCode] = {};
      categories.forEach(category => {
        grouped[unitCode][category] = items.filter(item =>
          item.unitCode === unitCode && item.category === category
        );
      });
    });
    return grouped;
  };

  const itemsByUnitAndCategory = groupByUnitAndCategory(items);

  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const selectedGstPlan = gstPlans.find(plan => plan.gstName === formData.gstPlan);

  const downloadExcelTemplate = async () => {
  try {

    // Fetch price categories
    let priceCategoryList = [];

    if (selectedUnitCode && selectedUnitCode !== "all") {

      const q = query(
        collection(db, "priceCategories"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", selectedUnitCode)
      );

      const snapshot = await getDocs(q);

      priceCategoryList = snapshot.docs.map(doc => doc.data());
    }

    // Fetch existing menu items
    let menuQuery;

    if (selectedUnitCode === "all") {

      menuQuery = query(
        collection(db, "companymenu"),
        where("companyCode", "==", companyCode)
      );

    } else {

      menuQuery = query(
        collection(db, "companymenu"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", selectedUnitCode)
      );
    }

    const menuSnapshot = await getDocs(menuQuery);

    let excelData = [];

    // If items exist → export them
    if (!menuSnapshot.empty) {

      excelData = menuSnapshot.docs.map(doc => {

        const item = doc.data();

        const row = {
  id: item.id || "",

  name: item.name || "",

  itemCode: item.itemCode || "",

  category: item.category || "",

  description: item.description || "",

  ingredients: item.ingredients || "",

  price: item.basePrice || item.price || "",

  type: item.type || "veg",

  available: item.available ?? true,

  imageUrl: item.imageUrl || "",

  staff: item.staff || "",

  unitCode: item.unitCode || "",

  unitName: item.unitName || "",

  companyCode: item.companyCode || "",

  companyName: item.companyName || "",

  gstPlan: item.gstPlan || "",

  gstPercentage: item.gstPercentage || 0,

  cgstAmount: item.cgstAmount || 0,

  sgstAmount: item.sgstAmount || 0,

  basePrice: item.basePrice || 0,

  createdAt: item.createdAt
    ? new Date(
        item.createdAt.seconds * 1000
      ).toLocaleString()
    : "",

  // Full categoryPrices map
  categoryPrices: JSON.stringify(
    item.categoryPrices || {}
  ),
};

        // Add dynamic category prices
       

        return row;
      });

    } else {

      // Fallback sample row if no items exist
      const templateRow = {
        name: "Paneer Butter Masala",
        itemCode: "PBM",
        category: "Main Course",
        description: "Creamy paneer curry",
        ingredients: "Paneer, Butter, Tomato",
        price: 250,
        type: "veg",
        available: true,
        imageUrl: "",
        staff: "staff001",
        unitCode:
          selectedUnitCode !== "all"
            ? selectedUnitCode
            : "UNIT001",
        gstPlan: "No GST"
      };

      // Add dynamic price categories
      priceCategoryList.forEach(cat => {

        if (cat.priceCategoryName === "Standard Price") return;

        templateRow[cat.priceCategoryName] = "";
      });

      excelData = [templateRow];
    }

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto column width
    const columnWidths = Object.keys(excelData[0] || {}).map(key => ({
      wch: Math.max(
        key.length,
        ...excelData.map(row =>
          row[key]
            ? row[key].toString().length
            : 10
        )
      ) + 2
    }));

    worksheet["!cols"] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "MenuTemplate"
    );

    // Download file
    XLSX.writeFile(
      workbook,
      `Menu_${selectedUnitCode || "All"}_Export.xlsx`
    );

    setSnackbar({
      open: true,
      message: menuSnapshot.empty
        ? "Template downloaded successfully"
        : "Menu exported successfully",
      severity: "success"
    });

  } catch (error) {

    console.error("Error downloading template:", error);

    setSnackbar({
      open: true,
      message: "Failed to download template",
      severity: "error"
    });
  }
};
const handleExcelImport = async (event) => {

  try {

    const file = event.target.files[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (e) => {

      const data = new Uint8Array(e.target.result);

      const workbook = XLSX.read(data, {
        type: "array"
      });

      const sheetName = workbook.SheetNames[0];

      const worksheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        raw: false,
        defval: "",
      });

      const cleanedData = jsonData.map((row) => {

        const cleanedRow = {};

        Object.keys(row).forEach((key) => {
          cleanedRow[key.trim()] = row[key];
        });

        return cleanedRow;
      });

      const validItems = [];
      const alreadyExisting = [];
      const rejected = [];

      for (const row of cleanedData) {

        const normalizedRow = {
          name: row.name?.toString().trim(),
          itemCode: row.itemCode?.toString().trim(),
          category: row.category?.toString().trim(),
          description: row.description?.toString().trim() || "",
          ingredients: row.ingredients?.toString().trim() || "",
          price: row.price,
          type: row.type?.toString().trim() || "veg",
          available: row.available,
          imageUrl: row.imageUrl?.toString().trim() || "",
          staff: row.staff?.toString().trim() || "",
          unitCode: row.unitCode?.toString().trim(),
          gstPlan: row.gstPlan?.toString().trim(),
          categoryPrices: {},
        };

        // Required validation
        if (
          !normalizedRow.name ||
          !normalizedRow.itemCode ||
          !normalizedRow.category ||
          normalizedRow.price === "" ||
          normalizedRow.price === null ||
          normalizedRow.price === undefined ||
          isNaN(Number(normalizedRow.price))
        ) {

          rejected.push({
            ...normalizedRow,
            reason: "Invalid required fields or price"
          });

          continue;
        }

        const selectedGstPlan = gstPlans.find(
          plan =>
            plan.gstName?.trim().toLowerCase() ===
            normalizedRow.gstPlan?.trim().toLowerCase()
        );

        if (!selectedGstPlan) {

          rejected.push({
            ...normalizedRow,
            reason: "Invalid GST Plan"
          });

          continue;
        }

        const existingQuery = query(
          collection(db, "companymenu"),
          where("companyCode", "==", companyCode),
          where("unitCode", "==", normalizedRow.unitCode),
          where("itemCode", "==", normalizedRow.itemCode)
        );

        const existingSnapshot = await getDocs(existingQuery);

        if (!existingSnapshot.empty) {

          alreadyExisting.push({
            ...normalizedRow,
            reason: "Item already exists"
          });

          continue;
        }

        const gstValues = calculateGSTValues(
          Number(normalizedRow.price),
          selectedGstPlan
        );

        const categoryQuery = query(
          collection(db, "priceCategories"),
          where("companyCode", "==", companyCode),
          where("unitCode", "==", normalizedRow.unitCode)
        );

        const categorySnapshot = await getDocs(categoryQuery);

        const categories = categorySnapshot.docs.map(doc => doc.data());

        categories.forEach(cat => {

          if (cat.priceCategoryName === "Standard Price") return;

          const excelValue = row[cat.priceCategoryName];

          if (
            excelValue !== undefined &&
            excelValue !== null &&
            excelValue !== ""
          ) {
            normalizedRow.categoryPrices[cat.priceCategoryName] =
              Number(excelValue).toFixed(2);
          }
        });

        const unitObj = units.find(
          unit =>
            unit.unitCode?.trim().toLowerCase() ===
            normalizedRow.unitCode?.trim().toLowerCase()
        );

        validItems.push({
          ...normalizedRow,
          price: gstValues.price,
          basePrice: gstValues.basePrice,
          gstPercentage: gstValues.gstPercentage,
          cgstAmount: gstValues.cgstAmount,
          sgstAmount: gstValues.sgstAmount,
          unitName: unitObj?.name || "Unknown Unit",
        });
      }

      setImportableItems(validItems);

      setExistingItems(alreadyExisting);

      setRejectedItems(rejected);

      setSelectedImportItems([]);

      setExcelImportDialog(true);
    };

    reader.readAsArrayBuffer(file);

  } catch (error) {

    console.error(error);

    setSnackbar({
      open: true,
      message: "Failed to import Excel",
      severity: "error"
    });
  }
};

const handleSelectImportItem = (index) => {

  setSelectedImportItems((prev) => {

    if (prev.includes(index)) {
      return prev.filter(i => i !== index);
    }

    return [...prev, index];
  });
};

const confirmExcelImport = async () => {

  try {

    for (const row of excelPreviewItems) {

      if (row.isDuplicate) continue;

      const newDocRef = doc(collection(db, "companymenu"));

      // Fetch price categories for this unit
const categoryQuery = query(
  collection(db, "priceCategories"),
  where("companyCode", "==", companyCode),
  where("unitCode", "==", row.unitCode)
);

const categorySnapshot = await getDocs(categoryQuery);

const categories = categorySnapshot.docs.map(doc => doc.data());

// Build final category prices
const finalCategoryPrices = {};

// Standard price
finalCategoryPrices["Standard Price"] = Number(row.price).toFixed(2);

categories.forEach(cat => {

  if (cat.priceCategoryName === "Standard Price") return;

  // Manual category from excel
  if (
    row.categoryPrices &&
    row.categoryPrices[cat.priceCategoryName] !== undefined &&
    row.categoryPrices[cat.priceCategoryName] !== ""
  ) {

    finalCategoryPrices[cat.priceCategoryName] =
      Number(
        row.categoryPrices[cat.priceCategoryName]
      ).toFixed(2);

  } else {

    // Auto calculate percentage categories
    const calculatedPrice =
      Number(row.price) +
      (
        Number(row.price) *
        Number(cat.percentageIncrement || 0)
      ) / 100;

    finalCategoryPrices[cat.priceCategoryName] =
      calculatedPrice.toFixed(2);
  }
});

      const newItem = {
  id: newDocRef.id,

  name: row.name || "",

  itemCode:
    row.itemCode ||
    generateItemCode(row.name),

  category: row.category || "",

  description: row.description || "",

  ingredients: row.ingredients || "",

  price: Number(row.price || 0),

  basePrice: Number(row.basePrice || row.price || 0),

  categoryPrices: finalCategoryPrices,

  gstPercentage: Number(row.gstPercentage || 0),

  cgstAmount: Number(row.cgstAmount || 0),

  sgstAmount: Number(row.sgstAmount || 0),

  type:
    row.type &&
    row.type.toString().trim().toLowerCase() === "nonveg"
      ? "nonveg"
      : "veg",

  available:
    row.available === false ||
    row.available === "false"
      ? false
      : true,

  imageUrl: row.imageUrl || "",

  staff: row.staff || "",

  unitCode: row.unitCode || "",

  unitName: row.unitName || "",

  gstPlan: row.gstPlan || "",

  companyCode,

  createdAt: new Date(),
};
console.log("Saving Item:", newItem);
      await setDoc(newDocRef, newItem);
    }

    setExcelPreviewDialog(false);

    setExcelPreviewItems([]);

    fetchCompanyItems();

    setSnackbar({
      open: true,
      message: "Excel menu imported successfully!",
      severity: "success"
    });

  } catch (error) {

    console.error(error);

    setSnackbar({
      open: true,
      message: "Failed to import Excel",
      severity: "error"
    });
  }
};

const fetchMenuTimings = async () => {
  try {
    if (!companyCode) return;

    let q;

    if (selectedUnitCode === "all") {
      q = query(
        collection(db, "preferences"),
        where("companyCode", "==", companyCode),
        where("preferenceType", "==", "Menu Timing")
      );
    } else {
      q = query(
        collection(db, "preferences"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", selectedUnitCode),
        where("preferenceType", "==", "Menu Timing")
      );
    }

    const snapshot = await getDocs(q);

    const timings = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    setMenuTimings(timings);

    console.log("Fetched menu timings:", timings);

  } catch (error) {
    console.error("Error fetching menu timings:", error);

    setSnackbar({
      open: true,
      message: "Failed to load menu timings.",
      severity: "error",
    });
  }
};

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center">
            <RestaurantMenuIcon fontSize="large" sx={{ mr: 2, color: "primary.main" }} />
            <Typography variant="h4" fontWeight="bold">Menu Manager</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="unit-filter-label">Filter by Unit</InputLabel>
              <Select
                labelId="unit-filter-label"
                value={selectedUnitCode}
                label="Filter by Unit"
                onChange={handleUnitChange}
                size="small"
              >
                <MenuItem value="all">All Units</MenuItem>
                {units.map(unit => (
                  <MenuItem key={unit.unitCode} value={unit.unitCode}>{unit.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {/*<Button
              variant="outlined"
              startIcon={<FileCopyIcon />}
              onClick={handleImportOpen}
              sx={{ textTransform: "none" }}
            >
              Import Master Menu
            </Button>*/}
            <Box display="flex" gap={2}>

  <Button
    variant="outlined"
    startIcon={<FileCopyIcon />}
    onClick={handleImportOpen}
    sx={{ textTransform: "none" }}
  >
    Import Master Menu
  </Button>
{/*
  <Button
    variant="contained"
    component="label"
  >
    Import From Excel

    <input
      type="file"
      hidden
      accept=".xlsx,.xls"
      onChange={handleExcelImport}
    />
  </Button>

  <Button
    variant="outlined"
    onClick={downloadExcelTemplate}
  >
    Download Template
  </Button>*/}

</Box>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          sx={{ mb: 4, textTransform: "none" }}
        >
          Add New Item
        </Button>

        {Object.entries(itemsByUnitAndCategory).map(([unitCode, /*categories*/ categoryMap]) => (
          <Box key={unitCode} mb={5}>
            <Typography variant="h5" fontWeight="medium" mb={2}>
              {units.find(u => u.unitCode === unitCode)?.name || "Unknown Unit"}
            </Typography>
            <Divider sx={{ mb: 3 }} />
            {categories.map((category) => {
              const categoryItems = /*categories*/categoryMap[category];
              if (!categoryItems || categoryItems.length === 0) {
                return (
                  <Box key={category} mb={4}>
                    <Typography variant="h6" mb={2}>{category}</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={4}>
                        <AddButton
                          variant="outlined"
                          onClick={() => handleOpen(category)}
                          aria-label={`Add new item to ${category}`}
                        >
                          <AddIcon sx={{ fontSize: 60, color: "primary.main" }} />
                          <Typography variant="h6" color="text.secondary">
                            Add Item
                          </Typography>
                        </AddButton>
                      </Grid>
                    </Grid>
                  </Box>
                );
              }

              return (
                <Box key={category} mb={4}>
                  <Typography variant="h6" mb={2}>{category}</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={3}>
                    {categoryItems.map((item) => (
                      <Grid item xs={12} sm={6} md={4} key={item.id}>
                        <StyledCard
                          elevation={3}
                          onClick={(e) => {
                            if (!e.target.closest(".MuiIconButton-root")) {
                              handleDetailClick(item);
                            }
                          }}
                        >
                          <CardMedia
                            component="img"
                            height="180"
                            image={item.imageUrl || "/placeholder.png"}
                            alt={item.name}
                            sx={{ objectFit: "cover" }}
                          />
                          <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                            <Box>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6" gutterBottom>{item.name}</Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    bgcolor: item.type?.toLowerCase() === "veg" ? "success.light" : "error.light",
                                    px: 1,
                                    py: 0.5,
                                    width: "72px",
                                    textAlign: "center",
                                    borderRadius: 1,
                                    color: "white",
                                  }}
                                >
                                  {/*{item.type === "veg" ? "VEG" : "NON-VEG"}*/}
                                  {item.type?.toLowerCase() === "veg"
                                  ? "VEG"
                                  : "NON-VEG"}
                                </Typography>
                              </Box>
                              <Typography
                                color="text.secondary"
                                variant="body2"
                                gutterBottom
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {truncateText(item.description, 50)}
                              </Typography>
                              {item.ingredients && (
                                <Typography
                                  color="text.secondary"
                                  variant="body2"
                                  gutterBottom
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  <strong>Ingredients:</strong> {truncateText(item.ingredients, 30)}
                                  {item.ingredients.length > 30 && (
                                    <Typography
                                      component="span"
                                      color="primary"
                                      variant="body2"
                                      sx={{ cursor: "pointer" }}
                                    >
                                      {" Read more"}
                                    </Typography>
                                  )}
                                </Typography>
                              )}
                            </Box>
                            <Box>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mt={0}>
                                <Typography variant="h6" color="primary">₹{item.price}</Typography>
                                <Box>
                                  <IconButton onClick={() => handleEditClick(item)} color="primary" size="small">
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton onClick={() => handleDeleteClick(item.id)} color="error" size="small">
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                              {!item.available && (
                                <Typography variant="body2" color="error" sx={{ mt: 1, fontStyle: "italic" }}>
                                  Currently unavailable
                                </Typography>
                              )}
                            </Box>
                          </CardContent>
                        </StyledCard>
                      </Grid>
                    ))}
                    <Grid item xs={12} sm={6} md={4}>
                      <AddButton
                        variant="outlined"
                        onClick={() => handleOpen(category)}
                        aria-label={`Add new item to ${category}`}
                      >
                        <AddIcon sx={{ fontSize: 60, color: "primary.main" }} />
                        <Typography variant="h6" color="text.secondary">
                          Add Item
                        </Typography>
                      </AddButton>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? "Edit Menu Item" : "Add Menu Item"}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Name"
            name="name"
            fullWidth
            value={formData.name}
            onChange={handleChange}
            required
          />
          <TextField
  margin="dense"
  label="Item Code"
  name="itemCode"
  fullWidth
  value={formData.itemCode}
  onChange={handleChange}
  inputProps={{ maxLength: 5 }}
  helperText="Auto-generated from name. Max 5 characters"
/>
          <TextField
            select
            margin="dense"
            label="Category"
            name="category"
            fullWidth
            value={formData.category}
            onChange={handleChange}
            required
          >
            {categories.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel id="unit-select-label">Unit</InputLabel>
            <Select
              labelId="unit-select-label"
              name="unitCode"
              value={formData.unitCode}
              label="Unit"
             // onChange={handleChange}
              onChange={(e) => {
      handleChange(e);

      const unitCode = e.target.value;

      if (unitCode) {
        fetchPriceCategories(unitCode);
      }

      setCategoryPrices({});
    }}
              disabled={selectedUnitCode !== "all"}
              required
            >
              {units.length > 0 ? (
                units.map(unit => (
                  <MenuItem key={unit.unitCode} value={unit.unitCode}>
                    {unit.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No units available
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={2}
            value={formData.description}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            label="Ingredients"
            name="ingredients"
            fullWidth
            value={formData.ingredients}
            onChange={handleChange}
            placeholder="Comma separated ingredients"
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel id="gst-plan-label">GST Plan</InputLabel>
            <Select
              labelId="gst-plan-label"
              name="gstPlan"
              value={formData.gstPlan}
              label="GST Plan"
              onChange={handleChange}
              required
            >
              {gstPlans.length > 0 ? (
                gstPlans.map(plan => (
                  <MenuItem key={plan.id} value={plan.gstName}>
                    {plan.gstName} ({plan.percentage}%)
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No GST plans available
                </MenuItem>
              )}
            </Select>
          </FormControl>
          {formData.gstPlan && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Inclusive: {selectedGstPlan?.inclusive ? "Yes" : "No"}
            </Typography>
          )}
          <TextField
            margin="dense"
            label="Standard Price"
            name="price"
            type="number"
            fullWidth
            value={formData.price}
            onChange={handleChange}
            required
            InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }}
            helperText={formData.gstPlan && selectedGstPlan?.inclusive ? "Enter price inclusive of GST" : "Enter base price (GST will be added)"}
          />
          {priceCategories.length > 0 && Number(formData.price) > 0 && (
            <Box mt={1}>
              {/*{priceCategories.map(cat => {*/}
              {priceCategories
  .filter(cat => cat.priceCategoryName !== "Standard Price")
  .map(cat => {
               // const isManual = cat.percentageIncrement === 0;
                return (
                 <TextField
                  key={cat.priceCategoryName}
                  margin="dense"
                  //label={`${cat.priceCategoryName} (${cat.percentageIncrement}%)`}
                  label={
  cat.percentageIncrement === 0
    ? cat.priceCategoryName
    : `${cat.priceCategoryName} (${cat.percentageIncrement}%)`
}
                  type="number"
                  fullWidth
                  //InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }}
                  value={categoryPrices[cat.priceCategoryName] ?? ""}
                  onChange={(e) => {
                    if (cat.percentageIncrement !== 0) return;

                    setCategoryPrices(prev => ({
                      ...prev,
                      [cat.priceCategoryName]: e.target.value,
                    }));
                  }}
                  //disabled={cat.percentageIncrement !== 0}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ mr: 1 }}>₹</Typography>
                    ),
                    readOnly: cat.percentageIncrement !== 0,
                  }}
                  helperText={
                    cat.percentageIncrement === 0
                      ? "Enter price manually"
                      : "Auto-calculated from standard price"
                  }
                  sx={{ mb: 1 }}
                />
                );
              })}
            </Box>
          )}
          <TextField
            select
            margin="dense"
            label="Type"
            name="type"
            fullWidth
            value={formData.type}
            onChange={handleChange}
            disabled={isPureVeg}
          >
            <MenuItem value="veg">Vegetarian</MenuItem>
            <MenuItem value="nonveg">Non-Vegetarian</MenuItem>
          </TextField>
          <TextField
            select
            margin="dense"
            label="Staff User ID"
            name="staff"
            fullWidth
            value={formData.staff}
            onChange={handleChange}
            required
            helperText={kitchenStaff.length === 0 ? "No kitchen staff available for this unit" : "Select a kitchen staff member"}
          >
            {kitchenStaff.length > 0 ? (
              kitchenStaff.map((userId) => (
                <MenuItem key={userId} value={userId}>
                  {userId}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                No kitchen staff available
              </MenuItem>
            )}
          </TextField>
                    {formData.available && (
            <TextField
              select
              margin="dense"
              label="Menu Timing"
              name="timing"
              fullWidth
              value={formData.timing}
              onChange={handleChange}
              required
            >
              {menuTimings.map((timing) => (
                <MenuItem
                  key={timing.id}
                  value={timing.preferenceName}
                >
                  {timing.preferenceName}
                </MenuItem>
              ))}
            </TextField>
          )}
          <FormControlLabel
            control={<Switch checked={formData.available} name="available" onChange={handleChange} color="success" />}
            label="Available for Order"
          />
          <TextField
            margin="dense"
            label="Image URL"
            name="imageUrl"
            fullWidth
            value={formData.imageUrl}
            onChange={handleChange}
            placeholder="Enter image URL"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          {/*<Button onClick={handleAdd} variant="contained" color="primary">
            {isEditing ? "Update Item" : "Add Item"}
          </Button>*/}
          <Button
  onClick={() => {

    if (isExcelPreviewEdit) {

      const updatedItems = [...excelPreviewItems];

       const updatedCategoryPrices = {
        ...categoryPrices,

        // Sync Standard Price with entered price
        "Standard Price": Number(formData.price).toFixed(2),
      };

      updatedItems[editingPreviewIndex] = {
        ...updatedItems[editingPreviewIndex],

        ...formData,

        price: Number(formData.price),

        categoryPrices: updatedCategoryPrices,
      };

      setExcelPreviewItems(updatedItems);

      setOpen(false);

      setExcelPreviewDialog(true);

      setIsExcelPreviewEdit(false);

      setEditingPreviewIndex(null);

      return;
    }

    handleAdd();
  }}
  variant="contained"
  color="primary"
>
  {isExcelPreviewEdit
    ? "OK"
    : isEditing
    ? "Update Item"
    : "Add Item"}
</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this menu item?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button color="error" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailDialog.open} onClose={handleDetailClose} maxWidth="md" fullWidth>
        <DialogTitle>{detailDialog.item?.name}
           {detailDialog.item?.itemCode && ` (${detailDialog.item?.itemCode})`}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
            <Box flexShrink={0}>
              <CardMedia
                component="img"
                height="200"
                width="300"
                image={detailDialog.item?.imageUrl || "/placeholder.png"}
                alt={detailDialog.item?.name}
                sx={{ objectFit: "cover", borderRadius: 1 }}
              />
            </Box>
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Unit:</strong> {detailDialog.item?.unitName || "Unknown Unit"}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Category:</strong> {detailDialog.item?.category}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Type:</strong> {detailDialog.item?.type === "veg" ? "Vegetarian" : "Non-Vegetarian"}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Description:</strong> {detailDialog.item?.description || "No description available"}
              </Typography>
              {detailDialog.item?.ingredients && (
                <Typography variant="body1" gutterBottom>
                  <strong>Ingredients:</strong> {detailDialog.item?.ingredients}
                </Typography>
              )}
              <Typography variant="body1" gutterBottom>
                <strong>Price:</strong> ₹{detailDialog.item?.price} {detailDialog.item?.gstPlan ? `(${detailDialog.item?.gstPlan.includes('inclusive') ? 'Inclusive' : 'Exclusive'} of ${detailDialog.item?.gstPercentage}% GST)` : ''}
              </Typography>
              {detailDialog.item?.basePrice && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Base Price:</strong> ₹{detailDialog.item?.basePrice.toFixed(2)}
                </Typography>
              )}
              {detailDialog.item?.categoryPrices &&
                Object.keys(detailDialog.item.categoryPrices).length > 0 && (
                <Box>
                  {Object.entries(detailDialog.item.categoryPrices).map(
                    ([categoryName, price]) => (
                      <Typography
                        key={categoryName}
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        <strong>{categoryName}:</strong> ₹{Number(price).toFixed(2)}
                      </Typography>
                    )
                  )}
                </Box>
              )}
              <Typography variant="body1" gutterBottom>
                <strong>Staff:</strong> {detailDialog.item?.staff || "Not assigned"}
              </Typography>
              {detailDialog.item?.gstPlan && (
                <Typography variant="body1" gutterBottom>
                  <strong>GST Plan:</strong> {detailDialog.item?.gstPlan} ({detailDialog.item?.gstPercentage}%)
                </Typography>
              )}
              {detailDialog.item?.cgstAmount && detailDialog.item?.sgstAmount && (
                <>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>CGST:</strong> ₹{detailDialog.item?.cgstAmount.toFixed(2)} ({(detailDialog.item?.gstPercentage / 2).toFixed(2)}%)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>SGST:</strong> ₹{detailDialog.item?.sgstAmount.toFixed(2)} ({(detailDialog.item?.gstPercentage / 2).toFixed(2)}%)
                  </Typography>
                </>
              )}
              <Typography
                variant="body1"
                color={detailDialog.item?.available ? "success.main" : "error.main"}
              >
                <strong>Availability:</strong> {detailDialog.item?.available ? "Available" : "Currently unavailable"}
              </Typography>
              {detailDialog.item?.timing && (
                <Typography variant="body1" gutterBottom>
                  <strong>Menu Timing:</strong> {detailDialog.item.timing}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailClose}>Close</Button>
          <Button
            onClick={() => handleEditClick(detailDialog.item)}
            variant="contained"
            color="primary"
          >
            Edit Item
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importDialog.open} onClose={handleImportClose} maxWidth="md" fullWidth>
        <DialogTitle>Import Master Menu</DialogTitle>
        <DialogContent>
          {importDialog.items.length === 0 ? (
            <Typography>No new items available in the master menu.</Typography>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>Select Items to Import</Typography>
              <List>
                {importDialog.items.map((item) => (
                  <ListItem key={item.id} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={importDialog.selected.includes(item.id)}
                      onChange={() => handleImportSelect(item.id)}
                    />
                    <ListItemText
                      primary={item.name}
                      secondary={`${item.category} - ${item.type === "veg" ? "Veg" : "Non-Veg"} - ₹${item.price}${item.staff ? ` - Staff: ${item.staff}` : ' - Staff: Not assigned'}`}
                    />
                  </ListItem>
                ))}
              </List>
              {importDialog.selected.length > 0 && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>Selected Items</Typography>
                  <List>
                    {importDialog.items
                      .filter((item) => importDialog.selected.includes(item.id))
                      .map((item) => (
                        <ListItem key={item.id} sx={{ display: 'flex', alignItems: 'center' }}>
                          <ListItemText primary={item.name} />
                          <IconButton
                            onClick={() => handleEditClick(item, true)}
                            color="primary"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </ListItem>
                      ))}
                  </List>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportClose}>Cancel</Button>
          <Button
            onClick={handleImportConfirm}
            variant="contained"
            color="primary"
            disabled={importDialog.selected.length === 0 || units.length === 0}
          >
            Import Selected
          </Button>
        </DialogActions>
      </Dialog>
      
         <Dialog
        open={gstUpdateDialog.open}
        onClose={() =>
          setGstUpdateDialog({
            open: false,
            applyToCategory: false,
          })
        }
      >
        <DialogTitle>Update GST for Category?</DialogTitle>
      
        <DialogContent>
          <Typography>
            GST selection has changed.
            Do you want to update GST for all items in this category?
          </Typography>
        </DialogContent>
      
        <DialogActions>
      
          <Button
            onClick={() => updateCategoryGST(false)}
          >
            No
          </Button>
      
          <Button
            variant="contained"
            onClick={() => updateCategoryGST(true)}
          >
            Yes
          </Button>
      
        </DialogActions>
      </Dialog>

<Dialog
  open={excelPreviewDialog}
  onClose={() => setExcelPreviewDialog(false)}
  maxWidth="md"
  fullWidth
>
  <DialogTitle>
    Excel Import Preview (Click an item to edit)
  </DialogTitle>

  <DialogContent>

    {excelPreviewItems.length === 0 ? (

      <Typography>
        No items found
      </Typography>

    ) : (

      <List>

        {excelPreviewItems.map((item, index) => (

         <ListItem
  key={index}
  divider
  button
  onClick={() => {
    setIsEditing(false);

    setFormData({
      name: item.name || "",
      itemCode: item.itemCode || "",
      category: item.category || "",
      description: item.description || "",
      ingredients: item.ingredients || "",
      price: item.price?.toString() || "",
      type: item.type || "veg",
      available:
        item.available === false ||
        item.available === "false"
          ? false
          : true,
      imageUrl: item.imageUrl || "",
      staff: item.staff || "",
      unitCode: item.unitCode || "",
      gstPlan:
  gstPlans.find(
    plan =>
      plan.gstName?.trim().toLowerCase() ===
      item.gstPlan?.trim().toLowerCase()
  )?.gstName || "",
    });

    setCategoryPrices(item.categoryPrices || {});

   setIsExcelPreviewEdit(true);

setEditingPreviewIndex(index);

setExcelPreviewDialog(false);

setOpen(true);
  }}
  sx={{
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  }}
>
            <ListItemText
              primary={`${item.name} (${item.itemCode})`}
              secondary={
                <>
                  <Typography variant="body2">
                    Category: {item.category}
                  </Typography>

                  <Typography variant="body2">
                    Price: ₹{item.price}
                  </Typography>

                  <Typography variant="body2">
                    Unit: {item.unitName}
                  </Typography>

                  <Typography
                    variant="body2"
                    color={
                      item.isDuplicate
                        ? "error"
                        : "success.main"
                    }
                  >
                    {item.isDuplicate
                      ? "Duplicate Item Code"
                      : "Ready to Import"}
                  </Typography>
                </>
              }
            />

          </ListItem>

        ))}

      </List>

    )}

  </DialogContent>

  <DialogActions>

    <Button
      onClick={() => setExcelPreviewDialog(false)}
    >
      Cancel
    </Button>

    <Button
      variant="contained"
      onClick={confirmExcelImport}
    >
      Confirm Import
    </Button>

  </DialogActions>
</Dialog>

<Dialog
  open={excelImportDialog}
  onClose={() => setExcelImportDialog(false)}
  maxWidth="md"
  fullWidth
>
  <DialogTitle>
    Excel Import Summary
  </DialogTitle>

  <DialogContent>

    {/* Importable Items */}
    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
      Select Items to Import
    </Typography>

    <List>

      {importableItems.map((item, index) => (

        <ListItem
          key={index}
          divider
        >

          <Checkbox
            checked={selectedImportItems.includes(index)}
            onChange={() => handleSelectImportItem(index)}
          />

          <ListItemText
            primary={`${item.name} (${item.itemCode})`}
            secondary={
              <>
                <Typography variant="body2">
                  Category: {item.category}
                </Typography>

                <Typography variant="body2">
                  Price: ₹{item.price}
                </Typography>

                <Typography variant="body2">
                  Unit: {item.unitName}
                </Typography>
              </>
            }
          />

        </ListItem>

      ))}

    </List>

    {/* Existing Items */}
    <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
      Already Existing Items
    </Typography>

    <List>

      {existingItems.map((item, index) => (

        <ListItem key={index} divider>

          <ListItemText
            primary={`${item.name} (${item.itemCode})`}
            secondary={item.reason}
          />

        </ListItem>

      ))}

    </List>

    {/* Rejected Items */}
    <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
      Rejected Items
    </Typography>

    <List>

      {rejectedItems.map((item, index) => (

        <ListItem key={index} divider>

          <ListItemText
            primary={`${item.name || "Unknown"} (${item.itemCode || "-"})`}
            secondary={item.reason}
          />

        </ListItem>

      ))}

    </List>

  </DialogContent>

  <DialogActions>

    <Button
      onClick={() => setExcelImportDialog(false)}
    >
      Cancel
    </Button>

    <Button
      variant="contained"
      onClick={() => {

        const previewItems = selectedImportItems.map(
          index => importableItems[index]
        );

        setExcelPreviewItems(previewItems);

        setExcelImportDialog(false);

        setExcelPreviewDialog(true);
      }}
      disabled={selectedImportItems.length === 0}
    >
      Import
    </Button>

  </DialogActions>

</Dialog>
      <Alerts
        snackbar={snackbar}
        handleClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        darkMode={false}
      />
    </Container>
  );
}